import { parseUtcTimestamp } from "../../services/dateTime";
/** Normalize the API's { data: [], meta_data } envelope at the UI boundary. */
export const unwrapBatch = (response) => {
  if (!response) return null;
  if (Array.isArray(response)) return response[0] || null;
  if (Object.prototype.hasOwnProperty.call(response, "data")) {
    return Array.isArray(response.data) ? response.data[0] || null : response.data;
  }
  return response;
};

const STATUS_LABELS = { created: "Created", wip: "WIP", active: "WIP", rework: "WIP", hold: "Hold", completed: "Completed", dispatched: "Completed" };
export const STATUS_LIST = ["Created", "WIP", "Hold", "Completed"];
export const normalizeStatus = status => STATUS_LABELS[String(status || "created").trim().toLowerCase()] || status;
const number = value => value !== null && value !== undefined && value !== "" && Number.isFinite(Number(value)) ? Number(value) : null;
const label = value => typeof value === "object" ? value?.name ?? value?.label ?? null : value;
const rowsFrom = response => Array.isArray(response) ? response : response?.data ?? response?.results ?? response?.items ?? [];
const metadataItem = (raw, meta) => meta?.item_details?.[`${raw.plant_id}:${raw.item_id}`] ?? meta?.item_details?.[raw.item_id];

export const toBatchListItem = (raw, meta = {}) => {
  const item = metadataItem(raw, meta);
  const quantity = number(raw?.quantity ?? raw?.batch_quantity);
  const status = normalizeStatus(raw?.status ?? (raw?.dispatch_on ? "completed" : "created"));
  const totalCycles = number(raw?.total_cycles) ?? ((number(raw?.ok_parts) ?? 0) + (number(raw?.hold_parts) ?? 0));
  return {
    uid: raw?.uid ?? raw?._id ?? raw?.id ?? raw?.batch_id,
    batchId: raw?.batch_id ?? raw?.batch_number ?? "-",
    partId: raw?.part_id ?? raw?.item_id ?? raw?.part_number ?? "-",
    partLabel: raw?.part_name ?? raw?.item_name ?? item?.item_name ?? raw?.part_id ?? raw?.item_id ?? "-",
    quantity,
    progress: Math.max(0, Math.min(100, Math.round(number(raw?.progress) ?? (status === "Completed" ? 100 : quantity ? totalCycles / quantity * 100 : 0)))),
    status,
    createdOn: raw?.created_on ?? raw?.created_at ?? null,
    lastUpdated: raw?.updated_at ?? raw?.last_updated ?? null,
    line: label(raw?.line ?? raw?.line_name ?? raw?.line_id),
    shift: label(raw?.shift ?? raw?.shift_name ?? raw?.shift_id),
    machine: label(raw?.machine ?? raw?.machine_name ?? raw?.machine_id),
    operator: label(raw?.operator_name ?? raw?.operator),
    okParts: number(raw?.ok_parts),
    reworkParts: number(raw?.rework_parts),
    rejectParts: number(raw?.reject_parts),
    holdParts: number(raw?.hold_parts),
    totalCycles,
    totalParts: number(raw?.total_parts) ?? quantity,
    dispatchOn: raw?.dispatch_on ?? null,
    raw,
  };
};

export const toBatchListResponse = response => {
  const rows = rowsFrom(response);
  const meta = response?.meta_data ?? response?.meta ?? {};
  return {
    rows: rows.map(row => toBatchListItem(row, meta)),
    meta,
    total: meta.total_records ?? meta.total ?? response?.total ?? rows.length,
    summary: {
      total: response?.summary?.total_batches ?? meta.total_records ?? rows.length,
      wip: response?.summary?.wip_batches ?? meta.wip_status_count ?? null,
      completed: response?.summary?.completed_batches ?? meta.completed_status_count ?? null,
      hold: response?.summary?.hold_parts ?? meta.hold_parts ?? null,
      rework: response?.summary?.rework ?? meta.rework_parts ?? null,
      reject: response?.summary?.reject ?? meta.reject_parts ?? null,
    },
  };
};

const timelineStep = (key, label, value) => {
  const timestamp = parseUtcTimestamp(value);
  return { key, label, timestamp, done: Boolean(timestamp) };
};
export const toBatchDetail = response => {
  const raw = unwrapBatch(response);
  if (!raw) return null;
  const row = toBatchListItem(raw, response?.meta_data);
  return {
    ...row,
    yieldRate: number(raw.yield_rate) ?? (row.totalCycles && row.okParts != null ? row.okParts / row.totalCycles * 100 : null),
    currentProcessTimeMinutes: number(raw.process_time_minutes),
    idealProcessTimeMinutes: number(raw.ideal_process_time_minutes) ?? (number(raw.production_duration) != null ? Number(raw.production_duration) / 60 : null),
    timeline: [
      timelineStep("created", "Created", raw.created_at),
      timelineStep("started", "Started", raw.started_at),
      timelineStep("rework", "Rework", raw.rework_at),
      timelineStep("completed", "Completed", raw.completed_at),
    ],
  };
};

export const normalizeLimits = limits => Object.fromEntries(Object.entries(limits || {}).map(([key, value]) => [key, {
  lsl: number(value?.lsl ?? value?.lower_specification_limit),
  lcl: number(value?.lcl ?? value?.lower_control_limit),
  ucl: number(value?.ucl ?? value?.upper_control_limit),
  usl: number(value?.usl ?? value?.upper_specification_limit),
}]));

export const toCycleRow = raw => {
  const disposition = raw.disposition?.toLowerCase() || null;
  const originalStatus = String(raw.status || "Unknown").toUpperCase();
  return {
    partId: String(raw.cycle_number ?? raw.cycle_no ?? raw.id),
    cycleNo: String(raw.cycle_no ?? raw.cycle_number ?? raw.id),
    temperature: number(raw.temperature ?? raw.temp), current: number(raw.current), time: number(raw.time ?? raw.cycle_time),
    originalStatus,
    status: disposition === "ok" ? "OK" : disposition === "rework" ? "Rework" : disposition === "reject" ? "Reject" : originalStatus,
    disposition, comment: raw.comment || "", history: raw.history || [],
    capturedAt: raw.captured_at, limits: normalizeLimits(raw.limits), raw,
  };
};
export const toCycleListResponse = response => ({
  rows: rowsFrom(response).map(toCycleRow),
  total: response?.meta_data?.total_records ?? rowsFrom(response).length,
  limits: normalizeLimits(response?.meta_data?.limits ?? response?.limits),
});

export const toDispatchHistoryRow = raw => ({
  uid: raw?.uid ?? raw?.id, batchId: raw?.batch_id ?? "-",
  partLabel: raw?.part_name ?? raw?.item_name ?? raw?.part_id ?? raw?.item_id ?? "-",
  dispatchedTo: raw?.dispatch_to ?? raw?.dispatched_to ?? "-",
  dispatchedBy: raw?.dispatch_by ?? raw?.dispatched_by ?? "-",
  dispatchedOn: raw?.dispatch_on ?? raw?.dispatched_on ?? null,
  quantity: raw?.quantity ?? raw?.batch_quantity ?? null, raw,
});
export const toDispatchHistoryResponse = response => ({
  rows: rowsFrom(response).map(toDispatchHistoryRow),
  total: response?.meta_data?.total_records ?? response?.meta?.total ?? rowsFrom(response).length,
});
